import React, {useMemo, useState} from "react";
import {
  Box,
  IconButton, InputAdornment,
  List,
  ListItem, ListSubheader,
  MenuItem,
  OutlinedInput,
  Paper,
  Select, TextField,
  Typography
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { RemoveCircleOutline } from "@mui/icons-material";
import { FeedbackRequest } from "../../../../types/FeedbackRequest";
import { WarningTooltip } from "../../../Tooltip";
import { InputContainerBox } from "../../../InputContainerBox";
import { Staff } from "../../../../types/Staff";
import SearchIcon from "@mui/icons-material/Search";

interface Props {
  items: FeedbackRequest[],
  peers: Staff[],
  add: () => void,
  apply: (index: number, event: any) => void,
  remove: (index: number) => void,
  readonly: boolean,
  duplicates: any,
  canSubmit: boolean
}

const outlineInputPops = {
  maxLength: 100
};

const inputStyles = {
  paddingRight: 0,
  width: 'calc(100% - 20px)'
}

export const PeerFeedbackRequestMobile: React.FC<Props> = (
  { items, peers, add, apply, remove, readonly, duplicates, canSubmit }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const containsText = (text: string, searchText: string) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const filteredPeers = useMemo(
    () => peers.filter((peer) => {
      const name = peer.firstName + ' ' + peer.lastName + (peer.preferredName ? ` (${peer.preferredName})` : '');
      return containsText(name, searchTerm);
    }), [searchTerm, peers]);

  const renderValue = (selectedId: number | undefined) => {
    if (!selectedId || selectedId === -1) {
      return '--- Select a peer ---';
    }
    const peer =  peers.find((peer) => peer.id === selectedId);
    return peer
      ? peer.firstName + ' ' + peer.lastName + (peer.preferredName ? ` (${peer.preferredName})` : '')
      : '';
  }
  return (
    <Paper sx={{ width: '100%', mt: 1 }}>
      <List dense sx={{ width: '100%', p: 0 }}>
        {
          items.map((data, index) => {
            const { peerStaffId, clientCompany, projectName } = data;

            return (
              <ListItem
                divider
                key={index}
                disablePadding
                sx={{ p: 2, pt: 1, pr: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
              >
                {!readonly &&
                  <Box sx={{ height: 20 }}>
                    <Box sx={{ position: 'absolute', right: 10, top: 10 }}>
                      <IconButton sx={{ pr: 2 }} color="primary" disabled={readonly} onClick={() => remove(index)}>
                        <RemoveCircleOutline />
                      </IconButton>
                    </Box>
                    <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                      {duplicates[index] && (<WarningTooltip title={"Duplicated entry at line: " + duplicates[index]} />)}
                    </Box>
                  </Box>
                }

                <Typography variant="caption">First and Last Name</Typography>
                <InputContainerBox>
                  <Select
                    disabled={readonly}
                    size='small'
                    name="peerStaffId"
                    sx={inputStyles}
                    value={peerStaffId}
                    onChange={(event) => apply(index, event)}
                    onClose={() => setSearchTerm("")}
                    renderValue={() => renderValue(peerStaffId)}
                    MenuProps={{
                      autoFocus: false,
                      PaperProps: {
                        style: { height: '200px', overflowY: 'scroll' }
                      }
                    }}
                  >
                    <ListSubheader>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Type to search..."
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          )
                        }}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== "Escape") {
                            e.stopPropagation();
                            setSearchTerm('');
                          }
                        }}
                      />
                    </ListSubheader>
                    <MenuItem value={-1}>--- Select a peer ---</MenuItem>
                    {filteredPeers.map((peer, key) => {
                      return (
                        <MenuItem
                          key={key}
                          value={peer.id}
                        >
                          {peer.firstName + ' ' + peer.lastName + (peer.preferredName ? ` (${peer.preferredName})` : '')}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {!peerStaffId && <WarningTooltip title={"Mandatory field."} />}
                </InputContainerBox>

                <Typography variant="caption" sx={{ mt: 1 }}>Company</Typography>
                <InputContainerBox>
                  <OutlinedInput
                    inputProps={outlineInputPops}
                    disabled={readonly}
                    size='small'
                    name="clientCompany"
                    sx={inputStyles}
                    autoComplete={'off'}
                    value={clientCompany}
                    onChange={(event) => apply(index, event)}
                  />
                  {!clientCompany && <WarningTooltip title={"Mandatory field."} />}
                </InputContainerBox>

                <Typography variant="caption" sx={{ mt: 1 }}>Project or Work Title</Typography>
                <InputContainerBox>
                  <OutlinedInput
                    inputProps={outlineInputPops}
                    disabled={readonly}
                    size='small'
                    sx={inputStyles}
                    autoComplete={'off'}
                    name="projectName"
                    value={projectName}
                    onChange={(event) => apply(index, event)}
                  />
                  {!projectName && <WarningTooltip title={"Mandatory field."} />}
                </InputContainerBox>
              </ListItem>
            );
          })}
        {readonly && !items.length ?
          <Typography variant="body2" sx={{ p: 2, color: '#aaa' }}>No data available.</Typography>
          :
          (canSubmit &&
            <ListItem key='-1' sx={{ py: 1 }}>
              <IconButton sx={{ p: 0 }} color="primary" disabled={readonly} onClick={add}>
                <AddCircleOutlineIcon />
              </IconButton>
            </ListItem>
          )
        }
      </List>
    </Paper>
  );
};
